import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Package, Plus, Pencil, Trash2, Search, Upload, Download,
  ChevronLeft, ChevronRight, Loader2, Database, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const emptyForm = () => ({
  sku: '', weight_lbs: 0, length_in: 0, width_in: 0, height_in: 0, box_quantity: 1,
});

const Skus = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState('create'); // create | edit
  const [form, setForm] = useState(emptyForm());

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // ---- Data fetchers ----
  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/v1/skus/stats/summary`);
      if (r.ok) setStats(await r.json());
    } catch (e) { /* ignore */ }
  }, []);

  const fetchPage = useCallback(async (p = page, q = query) => {
    setLoading(true);
    try {
      const url = `${API}/api/v1/skus?page=${p}&page_size=${PAGE_SIZE}&q=${encodeURIComponent(q)}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (e) {
      toast.error(`فشل تحميل المنتجات: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchPage(page, query); }, [page, query, fetchPage]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  // ---- Actions ----
  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(queryInput.trim());
  };

  const openCreate = () => {
    setEditMode('create');
    setForm(emptyForm());
    setEditOpen(true);
  };

  const openEdit = (row) => {
    setEditMode('edit');
    setForm({ ...row });
    setEditOpen(true);
  };

  const saveForm = async () => {
    const body = {
      sku: form.sku.trim().toUpperCase(),
      weight_lbs: Number(form.weight_lbs) || 0,
      length_in: Number(form.length_in) || 0,
      width_in: Number(form.width_in) || 0,
      height_in: Number(form.height_in) || 0,
      box_quantity: Math.max(1, parseInt(form.box_quantity, 10) || 1),
    };
    if (!body.sku) {
      toast.error('SKU مطلوب');
      return;
    }
    try {
      const url = editMode === 'edit'
        ? `${API}/api/v1/skus/${encodeURIComponent(body.sku)}`
        : `${API}/api/v1/skus`;
      const method = editMode === 'edit' ? 'PATCH' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      toast.success(editMode === 'edit' ? 'تم التحديث' : 'تم الإضافة');
      setEditOpen(false);
      fetchPage(page, query);
      fetchStats();
    } catch (e) {
      toast.error(`فشل الحفظ: ${e.message}`);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await fetch(`${API}/api/v1/skus/${encodeURIComponent(deleteTarget.sku)}`, {
        method: 'DELETE',
      });
      if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`);
      toast.success(`تم حذف ${deleteTarget.sku}`);
      setDeleteTarget(null);
      // If last item on page, step back
      const nextTotal = total - 1;
      const nextPageCount = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));
      const nextPage = Math.min(page, nextPageCount);
      if (nextPage !== page) setPage(nextPage);
      else fetchPage(page, query);
      fetchStats();
    } catch (e) {
      toast.error(`فشل الحذف: ${e.message}`);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/v1/skus/bulk-upload`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(
        `تم الرفع — إضافة: ${d.inserted}, تحديث: ${d.updated}, تخطي: ${d.skipped}`
      );
      fetchPage(1, query);
      setPage(1);
      fetchStats();
    } catch (err) {
      toast.error(`فشل الرفع: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleExport = () => {
    const q = query ? `?q=${encodeURIComponent(query)}` : '';
    window.open(`${API}/api/v1/skus/export/csv${q}`, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="skus-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Package className="h-7 w-7 text-primary" />
            إدارة المنتجات (SKUs)
          </h1>
          <p className="text-muted-foreground mt-1">
            إضافة وتعديل وحذف أوزان وأبعاد المنتجات المستخدمة في حساب الشحن
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleUpload}
            className="hidden"
            data-testid="sku-csv-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="sku-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <Upload className="h-4 w-4 mr-2" />}
            رفع CSV
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="sku-export-btn">
            <Download className="h-4 w-4 mr-2" />
            تصدير CSV
          </Button>
          <Button onClick={openCreate} data-testid="sku-add-btn">
            <Plus className="h-4 w-4 mr-2" />
            إضافة SKU
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="sku-stats">
          <StatCard icon={Database} label="إجمالي المنتجات" value={stats.total_skus} />
          <StatCard icon={Package}  label="لها أبعاد"       value={stats.with_dimensions} tone="emerald" />
          <StatCard icon={AlertTriangle} label="أبعاد ناقصة" value={stats.missing_dimensions} tone="amber" />
          <StatCard icon={AlertTriangle} label="وزن صفر"     value={stats.zero_weight} tone="rose" />
        </div>
      )}

      {/* Search + table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle>قائمة المنتجات</CardTitle>
              <CardDescription>
                إجمالي {total.toLocaleString()} — الصفحة {page} / {pageCount}
              </CardDescription>
            </div>
            <form onSubmit={submitSearch} className="flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="sku-search-input"
                  value={queryInput}
                  onChange={(e) => setQueryInput(e.target.value)}
                  placeholder="ابحث عن SKU..."
                  className="pl-8"
                />
              </div>
              <Button type="submit" variant="secondary" data-testid="sku-search-btn">
                بحث
              </Button>
              {query && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setQueryInput(''); setQuery(''); setPage(1); }}
                  data-testid="sku-clear-search-btn"
                >
                  مسح
                </Button>
              )}
            </form>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead className="text-right">الوزن (lbs)</TableHead>
                  <TableHead className="text-right">الطول</TableHead>
                  <TableHead className="text-right">العرض</TableHead>
                  <TableHead className="text-right">الارتفاع</TableHead>
                  <TableHead className="text-right">الكمية بالصندوق</TableHead>
                  <TableHead className="text-right">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10">
                      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                      جاري التحميل...
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                      لا توجد منتجات مطابقة
                    </TableCell>
                  </TableRow>
                ) : items.map((row) => {
                  const hasDims = row.length_in > 0 && row.width_in > 0 && row.height_in > 0;
                  return (
                    <TableRow key={row.sku} data-testid={`sku-row-${row.sku}`}>
                      <TableCell className="font-mono text-sm">
                        {row.sku}
                        {!hasDims && (
                          <Badge variant="outline" className="ml-2 text-xs border-amber-500/40 text-amber-600">
                            أبعاد ناقصة
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{row.weight_lbs}</TableCell>
                      <TableCell className="text-right">{row.length_in}</TableCell>
                      <TableCell className="text-right">{row.width_in}</TableCell>
                      <TableCell className="text-right">{row.height_in}</TableCell>
                      <TableCell className="text-right">{row.box_quantity}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => openEdit(row)}
                          data-testid={`sku-edit-btn-${row.sku}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => setDeleteTarget(row)}
                          data-testid={`sku-delete-btn-${row.sku}`}
                        >
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} من {total.toLocaleString()}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                data-testid="sku-prev-page-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> السابق
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount || loading}
                data-testid="sku-next-page-btn"
              >
                التالي <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit / Create Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="sku-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editMode === 'edit' ? 'تعديل منتج' : 'إضافة منتج جديد'}</DialogTitle>
            <DialogDescription>
              الأبعاد بالبوصة (in) والوزن بالباوند (lbs)
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="SKU" col="col-span-2">
              <Input
                value={form.sku}
                disabled={editMode === 'edit'}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                data-testid="sku-form-sku"
              />
            </Field>
            <Field label="الوزن (lbs)">
              <Input type="number" step="0.01" value={form.weight_lbs}
                     onChange={(e) => setForm({ ...form, weight_lbs: e.target.value })}
                     data-testid="sku-form-weight" />
            </Field>
            <Field label="الكمية/صندوق">
              <Input type="number" step="1" value={form.box_quantity}
                     onChange={(e) => setForm({ ...form, box_quantity: e.target.value })}
                     data-testid="sku-form-boxqty" />
            </Field>
            <Field label="الطول (in)">
              <Input type="number" step="0.01" value={form.length_in}
                     onChange={(e) => setForm({ ...form, length_in: e.target.value })}
                     data-testid="sku-form-length" />
            </Field>
            <Field label="العرض (in)">
              <Input type="number" step="0.01" value={form.width_in}
                     onChange={(e) => setForm({ ...form, width_in: e.target.value })}
                     data-testid="sku-form-width" />
            </Field>
            <Field label="الارتفاع (in)" col="col-span-2">
              <Input type="number" step="0.01" value={form.height_in}
                     onChange={(e) => setForm({ ...form, height_in: e.target.value })}
                     data-testid="sku-form-height" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>إلغاء</Button>
            <Button onClick={saveForm} data-testid="sku-save-btn">
              {editMode === 'edit' ? 'حفظ التعديلات' : 'إضافة'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="sku-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف المنتج</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف <span className="font-mono">{deleteTarget?.sku}</span>؟
              لن يتمكن النظام من حساب وزن/أبعاد هذا المنتج بعد الحذف.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="sku-delete-confirm-btn">
              تأكيد الحذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tone = 'default' }) => {
  const toneMap = {
    default: 'text-primary',
    emerald: 'text-emerald-500',
    amber: 'text-amber-500',
    rose: 'text-rose-500',
  };
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-1">{(value ?? 0).toLocaleString()}</p>
          </div>
          <Icon className={`h-8 w-8 ${toneMap[tone]}`} />
        </div>
      </CardContent>
    </Card>
  );
};

const Field = ({ label, col = '', children }) => (
  <div className={`space-y-1.5 ${col}`}>
    <Label>{label}</Label>
    {children}
  </div>
);

export default Skus;
